<script lang="ts">
    import InputError from '@/components/InputError.svelte';
    import { Label } from '@/components/ui/label/index.js';
    import * as Dialog from '@/components/ui/dialog';
    import { Button } from '@/components/ui/button';
    import { XIcon } from 'lucide-svelte';
    import type { Snippet } from 'svelte';
    import Debug from '@/components/Debug.svelte';
    import { cn } from '@/lib/utils';

    type Props = {
        label?: string;
        error?: string;
        isOpen?: boolean;
        value?: number | null | boolean | string;
        valLabel?: string | null;
        children: Snippet;
        required?: boolean;
        showValidation?: boolean;
        args?: Record<string, any>;
    };
    let {
        label,
        value = $bindable(),
        valLabel = $bindable(),
        error,
        isOpen = $bindable(false),
        children,
        required = false,
        showValidation = false,
        ...args
    }: Props = $props();

    // Show error styling when there's a server error OR when showValidation is true and empty
    const showError = $derived(error || (showValidation && (value == null || value === '')));
    // Show success styling when showValidation is true and filled (and no error)
    const showSuccess = $derived(!error && showValidation && value != null && value !== '');
</script>

<div class="flex flex-col gap-2 w-full">
    {#if label}
        <Label for={label}>{label}{#if required}<span class="text-red-500 ml-1">*</span>{/if}</Label>
    {/if}
    <div
        class={cn(
            "border-input w-full bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 rounded-md border text-base file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            showError && "input-error",
            showSuccess && "input-success"
        )}
    >
        <Dialog.Root bind:open={isOpen}>
            <Button
                variant="ghost"
                onclick={() => (isOpen = true)}
                class="h-full flex-1 justify-start truncate"
                disabled={args.disabled}
            >
                {#if valLabel == null}
                    <p class="text-muted-foreground">Select</p>
                {:else}
                    <p class="break-words">{valLabel}</p>
                {/if}
            </Button>
            <Dialog.Content class="max-h-[90svh] max-w-lg sm:max-w-xl md:max-w-3xl lg:max-w-4xl xl:max-w-6xl overflow-auto">
                <Dialog.Header class="space-y-4">
                    <Dialog.Title>Click on a row to select.</Dialog.Title>
                </Dialog.Header>
                <div class="max-h-90svh overflow-auto">
                    {@render children()}
                </div>
            </Dialog.Content>
        </Dialog.Root>
        {#if value != null}
            <Button
                variant="ghost"
                onclick={() => {
                    value = null;
                    valLabel = null;
                    isOpen = false;
                }}><XIcon></XIcon></Button
            >
        {/if}
    </div>
    <InputError message={error} />
</div>
